import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createAssessment, resumeAssessment, getDepthOptions } from '../api/assessment';
import { CenteredLayout } from '../components/PageLayout';
import Card from '../components/Card';
import Button from '../components/Button';
import Alert from '../components/Alert';
import useProfileGuard from '../hooks/useProfileGuard';
import ProfileNudgeModal from '../components/ProfileNudgeModal';

// 2026-05-09：题量 / 轮数从后端策略读取，不再前端硬编码
const DEPTH_META = [
  { value:'quick',    label:'快速版', desc:'快速了解 AI 能力概况' },
  { value:'standard', label:'标准版', desc:'全面评估，推荐首次测评', recommended:true },
  { value:'deep',     label:'深度版', desc:'深度分析，细化薄弱项' },
];

// V5（2026-05-19）：题量 30/60/90，对应 quick/standard/deep —— 见 docs/V5-升级规划.md §1.4。
// rounds 字段对齐后端 round_plan.round_count() —— 每能力轮 8 题，3/6/9 轮。
// 后端 getDepthOptions() 会覆盖；这里是后端不可达时的兜底默认。
const DEFAULT_DEPTH_OPTIONS = {
  quick:    { total_questions: 30, rounds: 3 },
  standard: { total_questions: 60, rounds: 6 },
  deep:     { total_questions: 90, rounds: 9 },
};

export default function AssessmentRulesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resumeData, setResumeData] = useState(null);
  const [depthOptions, setDepthOptions] = useState(DEFAULT_DEPTH_OPTIONS);

  const { guardOpen, checkAndGuard, closeGuard } = useProfileGuard();

  const isEnterprise = !!user?.enterprise_name;

  // 把 DEPTH_META 与后端返回的题量配置合并为渲染列表
  const DEPTHS = DEPTH_META.map(d => {
    const opt = depthOptions[d.value] ?? DEFAULT_DEPTH_OPTIONS[d.value];
    return {
      ...d,
      count: `${opt.total_questions}题`,
      rounds: `${opt.rounds}轮`,
      time: `${Math.round(opt.total_questions / 2)}min`,
    };
  });

  useEffect(() => {
    // V5/M6b：极简画像不再 gate 测评。历史 Phase G 的 job_category_code 校验已随
    // 后端 migration 0013 删除字段一并下线。

    resumeAssessment().then(({ data }) => {
      if (data.has_unfinished) setResumeData(data.assessment);
    }).catch(() => {});

    getDepthOptions()
      .then(({ data }) => { if (data?.options) setDepthOptions(data.options); })
      .catch(() => { /* 静默失败保留默认 */ });
  }, []); // eslint-disable-line

  const handleStart = async () => {
    // 已有未完成测评时直接跳转，不再 POST —— 避免 console 出现 409 红色报错
    // 继续未完成测评不被 guard 拦（已 in-progress 的画像快照已存）
    if (resumeData) {
      navigate(`/assessment/${resumeData.id}`);
      return;
    }
    // 先 setLoading(true) 让按钮 disabled,再 guard —— 避免 guard 等待期间用户重复点击
    setError(''); setLoading(true);
    const status = await checkAndGuard();
    if (status !== 'pass') {
      setLoading(false);
      if (status === 'blocked_error') setError('暂时无法读取画像，请稍后重试');
      // blocked_incomplete: hard modal 已弹,不需要 setError
      // blocked_auth: axios 拦截器接管跳 /login,不显示任何提示
      return;
    }
    try {
      // 企业模式：不传 depth，由后端按 enterprise.rules.depth 决定
      // 个人模式：传员工自选档位
      const payload = isEnterprise ? {} : { depth: selected };
      const { data } = await createAssessment(payload);
      navigate(`/assessment/${data.id}`);
    } catch (err) {
      // 竞态兜底：如果 resume 轮询和用户点击之间产生了新 in-progress，仍走跳转分支
      if (err.response?.status === 409) {
        if (err.response.data?.assessment_id) {
          navigate(`/assessment/${err.response.data.assessment_id}`);
          return;
        }
        setError(err.response?.data?.detail || '创建测评失败，请重试');
      } else {
        setError(err.response?.data?.detail || '创建测评失败，请重试');
      }
    } finally { setLoading(false); }
  };

  return (
    <CenteredLayout maxWidth={620}>
      <div style={{textAlign:'center',marginBottom:32}}>
        <h1 style={{fontSize:26,fontWeight:800,marginBottom:8,color:'#0e1730',letterSpacing:'0.01em'}}>开始测评</h1>
        <p style={{color:'#6a7596',fontSize:13.5,margin:0}}>
          {isEnterprise ? `企业模式 · ${user.enterprise_name}` : '个人模式 · 评估你在 8 项能力 + 3 项底层素质上的水平'}
        </p>
      </div>

      {resumeData && (
        <Alert type="info">
          你有一次未完成的测评。
          <button onClick={()=>navigate(`/assessment/${resumeData.id}`)}
            style={{marginLeft:8,background:'none',border:'none',color:'var(--color-secondary)',cursor:'pointer',fontWeight:600,fontSize:13}}>
            继续上次测评 →
          </button>
        </Alert>
      )}

      {error && <Alert type="error">{error}</Alert>}

      <Card>
        {!isEnterprise ? (
          <>
            <h3 id="depth-label" style={{fontSize:15,fontWeight:800,marginBottom:14,color:'#0e1730',letterSpacing:'0.01em'}}>选择测评深度</h3>
            <div role="radiogroup" aria-labelledby="depth-label" style={{display:'flex',flexDirection:'column',gap:10,marginBottom:24}}>
              {DEPTHS.map(d => {
                const isSelected = selected===d.value;
                return (
                <div
                  key={d.value}
                  role="radio"
                  tabIndex={isSelected ? 0 : -1}
                  aria-checked={isSelected}
                  onClick={()=>setSelected(d.value)}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      setSelected(d.value);
                    }
                  }}
                  style={{
                    border:`1.5px solid ${isSelected?'#1f53e6':'#d6e0f5'}`,
                    borderRadius:11, padding:'15px 20px', cursor:'pointer',
                    background:isSelected?'#f3f7ff':'#fff',
                    transition:'all 0.15s ease', display:'flex', alignItems:'center', gap:16,
                    outline:'none',
                  }}
                  onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(31,83,230,0.18)'; }}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{
                    width:20,height:20,borderRadius:'50%',border:`2px solid ${isSelected?'#1f53e6':'#c1d0ed'}`,
                    background:isSelected?'#1f53e6':'#fff',flexShrink:0,
                    display:'flex',alignItems:'center',justifyContent:'center',
                  }}>
                    {isSelected && <div style={{width:8,height:8,borderRadius:'50%',background:'#fff'}}/>}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontWeight:700,fontSize:14.5,color:'#0e1730'}}>{d.label}</span>
                      {d.recommended && <span style={{fontSize:10.5,background:'#1f53e6',color:'#fff',padding:'2px 8px',borderRadius:5,fontWeight:600,letterSpacing:'0.04em'}}>推荐</span>}
                    </div>
                    <div style={{fontSize:12.5,color:'#6a7596',marginTop:3}}>{d.desc}</div>
                  </div>
                  <div style={{textAlign:'right',fontSize:12.5,color:'#6a7596'}}>
                    <div style={{fontWeight:700,color:'#0e1730',fontFamily:'Consolas, "SF Mono", monospace'}}>{d.count}</div>
                    <div style={{fontFamily:'Consolas, "SF Mono", monospace'}}>{d.rounds} · {d.time}</div>
                  </div>
                </div>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{marginBottom:24}}>
            <Alert type="info">测评规则由企业统一配置，无需单独选择深度。</Alert>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {[{label:'模式',val:'企业模式'},{label:'企业',val:user.enterprise_name}].map(i=>(
                <div key={i.label} style={{background:'var(--color-bg-page)',borderRadius:'var(--radius-sm)',padding:'12px 16px'}}>
                  <div style={{fontSize:12,color:'var(--color-text-muted)',marginBottom:4}}>{i.label}</div>
                  <div style={{fontWeight:600}}>{i.val}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{borderTop:'1px dashed #d6e0f5',paddingTop:18}}>
          <p style={{fontSize:12.5,color:'#6a7596',marginBottom:14,lineHeight:1.6}}>
            测评过程中可以随时退出，下次登录可继续作答；中途跳过的题目不会计入分母。
          </p>
          <Button fullWidth size="lg" onClick={handleStart} loading={loading}>开始测评</Button>
        </div>
      </Card>
      <ProfileNudgeModal mode="hard" open={guardOpen} onClose={closeGuard} />
    </CenteredLayout>
  );
}
